<script lang="ts" setup>
    type HeaderNavigationMobileExposed = {
        handleMenu: () => void;
    };

    const { t } = useI18n();
    const { urlSegment, country, lang } = useLanguage();
    const countryLanguages = useCountryLanguages();
    const { isFachportal, isPtaFamily } = usePlatformMeta();
    const { disableSmoothScroll } = useDisableSmoothScroll();

    const matchedConfig = useCurrentSectionConfig();
    const bgColorClass = ref('--bg-color-default');
    const headerBg = useHeaderBgColor();
    const route = useRoute();

    const { data: story } = useConfig();

    const { isLocked, loginModalController, wrapNavigate } = useLockLinkGuard({
        onAllowed: () => handleMenu(),
        story
    });

    watch(
        () => route.fullPath,
        () => {
            useControlHeroStrip(false); // reset heroStrip, doing it here because PageComponents doesn't hook the watch on useRoute

            nextTick(() => {
                scheduleThresholdUpdate();
            });

            if (isMenuOpen.value) {
                closeMenu();
            }
        }
    );

    const currentSectionPath = computed(() => {
        if (!matchedConfig.value) return '';
        return resolveSectionPath(matchedConfig.value, lang.value, countryLanguages);
    });
    const logoLink = computed(() => buildLocalizedPath(country.value, lang.value, currentSectionPath.value ?? ''));

    const router = useRouter();
    const sideMenuController = useSideMenuModal();

    const { isKeycloakAuthenticated } = useLoginState();

    const isCheckout = computed(() => router.currentRoute.value.path === `/${urlSegment.value}/shop/checkout`);

    const hasHero = computed(() => {
        if (!lang.value || !urlSegment.value) return route.path === '/';

        const normalizedRoutePath = route.path.replace(/\/+$/, '') || '/';
        return normalizedRoutePath === logoLink.value || normalizedRoutePath === '/';
    });

    const slots = useSlots();

    const hasCustomUserActions = computed(() => {
        const slot = slots['custom-user-actions'];
        const content = slot?.();
        return content && content.length > 0;
    });

    const headerEl = ref<HTMLElement | null>(null);
    const headerCoreWrapperEl = ref<HTMLElement | null>(null);
    const headerCoreEl = ref<HTMLElement | null>(null);
    const header = computed(() => story.value?.content.header?.[0]);

    const headerTestId = computed(() => {
        // 'isPostLoginHeader' is only defined in code to determine if this is a post-login header
        const headerValue = header.value as { isPostLoginHeader?: boolean };
        return headerValue?.isPostLoginHeader ? 'layout-header-post-login' : 'layout-header';
    });

    const hasSearched = ref(false);
    provide('hasSearched', hasSearched);

    const mobileNav = ref<HeaderNavigationMobileExposed | null>(null);
    const backButtonRef = ref<HTMLElement | null>(null);

    const isSecondLayerOpen = ref(false);
    const firstLayerLink = ref<HTMLElement | null>(null);
    const subLayer = ref();
    const modal = ref();
    const isMenuOpen = ref(false);

    const toggleSecondLayer = (value: object, event: Event) => {
        if (!isSecondLayerOpen.value) {
            firstLayerLink.value = event.currentTarget as HTMLElement;
        }
        isSecondLayerOpen.value = !isSecondLayerOpen.value;
        subLayer.value = value;

        nextTick(() => {
            if (isSecondLayerOpen.value && backButtonRef.value) {
                backButtonRef.value.focus();
            } else if (firstLayerLink.value) {
                firstLayerLink.value.focus();
            }
        });
    };

    const validHeaderFocus = ref(false);
    const checkHeaderFocus = (e: Event) => {
        if (headerEl.value && headerEl.value.contains(e.target as Node)) {
            validHeaderFocus.value = true;
        } else {
            validHeaderFocus.value = false;
        }
    };

    watch(validHeaderFocus, () => {
        if (isMenuOpen.value) {
            handleMenu();
        }
    });

    watch(isMenuOpen, (state) => {
        // use as debounce, to ensure modal is in DOM
        if (state) {
            requestAnimationFrame(() => {
                calcMenuHeight();
            });
        } else if (modal.value) {
            modal.value.style.height = '';
        }
    });

    watch(hasSearched, (state) => {
        if (state && isMenuOpen.value) {
            handleMenu();
        }
    });

    const isDesktop = ref(false);
    const checkViewportSize = () => {
        if (window.innerWidth >= 1024) {
            isDesktop.value = window.innerWidth >= 1024;

            if (isMenuOpen.value) {
                handleMenu();
            }
        } else {
            isDesktop.value = false;
        }
    };

    const isSticky = ref(false);
    const visible = ref(true);

    const isClient = useClientGate(true);
    const showLoginTeaser = useClientGate(
        computed(
            () =>
                Boolean(header.value?.loginTeaser?.length) &&
                (isFachportal.value || isPtaFamily.value) &&
                !isKeycloakAuthenticated.value
        )
    );
    const showSearch = computed(
        () =>
            Boolean(header.value?.enableSearch) &&
            !isCheckout.value &&
            (isFachportal.value || matchedConfig.value?.professionalPortalProductMount === true) &&
            (!hasHero.value || (!isSticky.value && visible.value))
    );

    let lastScroll = 0;
    let upScrollDelta = 0;
    const threshold = 180;
    const currentScroll = ref(0);
    const stickyEnterThreshold = ref(0);
    const stickyExitThreshold = ref(0);
    const headerCoreHeight = ref(0);
    const headerCoreTopOffset = ref(0);
    const allowTransition = ref(false);
    let headerResizeObserver: ResizeObserver | undefined;
    let thresholdUpdateFrame: number | null = null;

    // the sticky header is handled with different scroll values so the threshold for scrolling up is smoother
    const handleScroll = () => {
        const current = window.scrollY;
        currentScroll.value = current;
        const scrollingDown = current > lastScroll;
        const scrollingUp = current < lastScroll;

        if (scrollingDown) {
            upScrollDelta = 0;
            if (current >= stickyEnterThreshold.value) {
                isSticky.value = true;
            }

            if (current > stickyEnterThreshold.value) {
                visible.value = false;
            }
        } else if (scrollingUp) {
            upScrollDelta += lastScroll - current;
            if (upScrollDelta > threshold && current > stickyExitThreshold.value) {
                visible.value = true;
            }

            if (isSticky.value && current <= stickyExitThreshold.value) {
                isSticky.value = false;
                visible.value = true;
            }
        }

        lastScroll = current;
    };

    let ticking = false;

    const throttledScroll = () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                handleScroll();
                ticking = false;
            });
            ticking = true;
        }
    };

    const shouldAnimateStickyTransform = computed(() => currentScroll.value > stickyExitThreshold.value + threshold);

    watch(
        isSticky,
        () => {
            updateHeaderCoreMetrics();

            if (isSticky.value) {
                setTimeout(() => {
                    if (isSticky.value) {
                        // fallback if scrolling between the thresholds
                        allowTransition.value = true;
                    } else {
                        allowTransition.value = false;
                    }
                }, 50); // super fast timeout to ensure the transition doesn't fire instantly
            } else {
                allowTransition.value = false;
            }

            if (isMenuOpen.value && modal.value) {
                calcMenuHeight();
            }
        },
        { flush: 'post' }
    );

    const updateHeaderCoreMetrics = () => {
        const wrapperRect = headerCoreWrapperEl.value?.getBoundingClientRect();

        headerCoreHeight.value = headerCoreEl.value?.offsetHeight ?? 0;
        headerCoreTopOffset.value = Math.max(wrapperRect?.top ?? 0, 0);
    };

    const scheduleThresholdUpdate = () => {
        if (thresholdUpdateFrame !== null) {
            return;
        }

        thresholdUpdateFrame = window.requestAnimationFrame(() => {
            thresholdUpdateFrame = null;
            calcThreshold();
        });
    };

    const calcThreshold = () => {
        const searchEl = headerEl.value?.querySelector<HTMLElement>('.bg-strip.--has-search');
        const baseEnterThreshold = headerEl.value ? headerEl.value.clientHeight * 1.25 : 0;
        const wrapperRect = headerCoreWrapperEl.value?.getBoundingClientRect();
        const baseExitThreshold = wrapperRect ? wrapperRect.top + window.scrollY : 0;

        updateHeaderCoreMetrics();
        stickyEnterThreshold.value = baseEnterThreshold;
        stickyExitThreshold.value = baseExitThreshold;

        if (header.value?.enableSearch && hasHero.value && searchEl) {
            stickyEnterThreshold.value += searchEl.clientHeight;
            stickyExitThreshold.value += searchEl.clientHeight;
        }
    };

    const stickyClassBindings = computed(() => ({
        '--sticky-hidden': isSticky.value,
        '--sticky-visible': visible.value && isSticky.value,
        '--sticky-transition': allowTransition.value,
        '--sticky-transform-transition': allowTransition.value && shouldAnimateStickyTransform.value
    }));
    const bgStripClassBindings = computed(() => ({
        '--has-search': showSearch.value,
        '--has-hero': hasHero.value && !isSticky.value && visible.value,
        '--sticky': isSticky.value
    }));
    const headerCoreWrapperStyle = computed(() =>
        isSticky.value && headerCoreHeight.value ? { height: `${headerCoreHeight.value}px` } : undefined
    );
    const headerCoreStyle = computed(() => ({
        top: `${headerCoreTopOffset.value}px`
    }));

    const handleMenu = () => {
        if (mobileNav.value) {
            mobileNav.value.handleMenu();
            //reduce flicker when closing because of debounce
            window.setTimeout(() => (isSecondLayerOpen.value = false), 100);
        }

        disableSmoothScroll();
    };

    const closeMenu = () => {
        if (isMenuOpen.value) {
            handleMenu();
        }

        disableSmoothScroll();
    };

    const onModalClick = (e: MouseEvent) => {
        if ((e.target as HTMLElement).closest('a[href]')) {
            closeMenu();
        }
    };

    const calcMenuHeight = () => {
        const offset = isSticky.value ? headerCoreHeight.value : (headerEl.value?.clientHeight ?? 0);
        modal.value.style.height = `${document.documentElement.clientHeight - offset}px`;
    };

    const addressContainerClasses = computed(() => {
        const customAddressWidth = header.value?.contactAddressWidth === 'fullWidth' ? '--full-width' : '';
        const customAddressAlignment = `--align-${header.value?.contactAddressAlignment ?? 'left'}`;
        const customAddressAlignmentMobile = header.value?.contactAddressAlignmentMobile
            ? `--align-mobile-${header.value?.contactAddressAlignmentMobile}`
            : '';

        return [customAddressWidth, customAddressAlignment, customAddressAlignmentMobile];
    });

    const handleResize = () => {
        checkViewportSize();
        scheduleThresholdUpdate();
        if (modal.value) calcMenuHeight();
    };

    onMounted(() => {
        watch(
            () => headerBg.value,
            (newColor) => {
                bgColorClass.value = newColor ? `--bg-color-${newColor}` : '--bg-color-default';
            },
            { immediate: true }
        );

        checkViewportSize();

        // sticky setup
        headerResizeObserver = new ResizeObserver(() => {
            scheduleThresholdUpdate();
        });

        if (headerEl.value) {
            headerResizeObserver.observe(headerEl.value);
        }

        nextTick(() => {
            scheduleThresholdUpdate();
        });

        useEventListener('resize', handleResize);
        useEventListener('keyup', checkHeaderFocus);
        useEventListener('scroll', throttledScroll);
    });

    onBeforeUnmount(() => {
        if (thresholdUpdateFrame !== null) {
            window.cancelAnimationFrame(thresholdUpdateFrame);
            thresholdUpdateFrame = null;
        }

        headerResizeObserver?.disconnect();
        headerResizeObserver = undefined;
    });
</script>

<template>
    <header
        v-if="header"
        ref="headerEl"
        class="header"
        :data-testid="headerTestId"
        :class="[bgColorClass, { '--row-layout': header.rowLayout }]"
        aria-label="top-navigation"
    >
        <div
            v-if="header.contactAddress"
            class="contact-line"
            :class="addressContainerClasses"
        >
            <div class="contact-line-content">
                <span
                    v-if="header.contactAddressAdditionalText"
                    class="contact-line-text"
                >
                    {{ header.contactAddressAdditionalText }}
                </span>

                <a :href="'mailto:' + header.contactAddress">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <rect
                            width="20"
                            height="16"
                            x="2"
                            y="4"
                            rx="2"
                        />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                    {{ header.contactAddress }}
                </a>

                <div
                    v-if="header.contactPrimaryButtonLink || header.contactSecondaryButtonLink"
                    class="button-wrapper"
                >
                    <a
                        v-if="header.contactPrimaryButtonLink"
                        :href="header.contactPrimaryButtonLink"
                        class="primary-button --medium --no-icon"
                    >
                        <span>{{ header.contactPrimaryButtonText }}</span>
                    </a>

                    <a
                        v-if="header.contactSecondaryButtonLink"
                        :href="header.contactSecondaryButtonLink"
                        class="primary-button --medium --no-icon --alternate-button"
                    >
                        <span>{{ header.contactSecondaryButtonText }}</span>
                    </a>
                </div>
            </div>
        </div>

        <div class="relative-scope">
            <StoryblokComponentWrapper
                v-if="showLoginTeaser && header.loginTeaser?.[0]"
                :blok="header.loginTeaser[0]"
            />

            <template v-if="header.promotionTeaser?.length">
                <StoryblokComponentWrapper
                    v-for="promoTeaser in header?.promotionTeaser"
                    :key="promoTeaser._uid"
                    :blok="promoTeaser"
                />
            </template>

            <div
                ref="headerCoreWrapperEl"
                :style="headerCoreWrapperStyle"
            >
                <div
                    ref="headerCoreEl"
                    class="header-core"
                    :class="[bgColorClass, stickyClassBindings]"
                    :style="headerCoreStyle"
                >
                    <div
                        class="max-width"
                        :class="{ '--gap': header.rowLayout && (header.showLanguageSwitcher || hasCustomUserActions) }"
                    >
                        <div
                            class="left-side"
                            :class="{ '--simple-header': isCheckout }"
                        >
                            <div
                                class="portal-wrapper"
                                :class="{ '--no-switch': !header.enableHubSwitcher }"
                            >
                                <div
                                    v-if="header.enableHubSwitcher && !isCheckout"
                                    class="portal-switcher-desktop"
                                >
                                    <HeaderHubSwitcher :hubs="header.hubs" />
                                </div>

                                <HeaderLogo
                                    v-if="header.logo"
                                    :image="header.logo"
                                    :to="logoLink"
                                    @click="closeMenu"
                                />

                                <MetaNavigation
                                    v-if="
                                        story?.content.metaNavigationLinks?.length &&
                                        story.content.metaNavigationLinks.length > 0 &&
                                        !isCheckout &&
                                        isDesktop
                                    "
                                    :config="story.content"
                                    @click="closeMenu"
                                />
                            </div>

                            <HeaderNavigationDesktop
                                v-if="!isCheckout"
                                :is-desktop="isDesktop"
                                :navigation="header.navigation"
                            />
                        </div>

                        <div
                            class="right-side"
                            :class="{ '--no-user-actions': !hasCustomUserActions }"
                        >
                            <HeaderLanguagePicker v-if="header.showLanguageSwitcher" />

                            <div class="user-actions">
                                <template v-if="!sideMenuController.isOpen.value">
                                    <slot name="custom-user-actions"> </slot>
                                </template>

                                <HeaderNavigationMobile
                                    ref="mobileNav"
                                    v-model:is-menu-open="isMenuOpen"
                                    :navigation="header.navigation"
                                    :hubs="header.hubs"
                                    :enable-hub-switcher="header.enableHubSwitcher && !isCheckout"
                                />
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="!showSearch && !(header?.hideBgStrip || hideHeroStrip)"
                        class="bg-strip"
                        :class="bgStripClassBindings"
                    />
                </div>
            </div>
        </div>

        <div
            v-if="showSearch && !(header?.hideBgStrip || hideHeroStrip)"
            class="bg-strip"
            :class="bgStripClassBindings"
        >
            <HeaderSearch v-if="showSearch" />
        </div>

        <Transition name="modal">
            <div
                v-if="isMenuOpen && !isDesktop"
                ref="modal"
                class="modal"
                :class="{ '--fixed': isSticky }"
                :style="isSticky ? { top: `${headerCoreHeight}px` } : undefined"
                @click="onModalClick"
            >
                <div class="nav-wrapper">
                    <ul>
                        <li
                            v-for="blok in header.navigation"
                            :key="blok._uid"
                        >
                            <button
                                v-if="
                                    (blok.secondLayer && blok.secondLayer.length >= 1) ||
                                    (blok.additionalBlocks && blok.additionalBlocks.length > 0)
                                "
                                class="custom-select-button"
                                :tabindex="isSecondLayerOpen ? '-1' : ''"
                                @click="toggleSecondLayer(blok, $event)"
                            >
                                <span class="select-title">{{ blok.linkName }}</span>

                                <div
                                    class="select-value"
                                    :class="{ '--active': isSecondLayerOpen }"
                                >
                                    <img
                                        class="icon"
                                        src="~/assets/icons/chevron-right-black.svg"
                                        alt="Filter"
                                        aria-hidden="true"
                                    />
                                </div>
                            </button>

                            <NuxtLink
                                v-else-if="blok.link"
                                v-slot="{ href, navigate }"
                                :to="useStoryblokUrl(blok.link)"
                                custom
                            >
                                <a
                                    :href="href"
                                    class="custom-select-button"
                                    :tabindex="isSecondLayerOpen ? '-1' : ''"
                                    :aria-disabled="isClient && isLocked(blok.link) && !isKeycloakAuthenticated"
                                    @click="wrapNavigate(navigate, blok.link).onClick($event)"
                                    @keydown.enter="wrapNavigate(navigate, blok.link).onEnter($event)"
                                    @keydown.space="wrapNavigate(navigate, blok.link).onSpace($event)"
                                >
                                    <span>{{ blok.linkName }}</span>
                                    <img
                                        v-if="isClient && blok.link?.story?.content?.locked && !isKeycloakAuthenticated"
                                        src="~/assets/icons/lock-keyhole.svg"
                                        class="navi-lock first-layer-lock"
                                        aria-hidden="true"
                                    />
                                </a>
                            </NuxtLink>
                        </li>
                    </ul>

                    <Transition name="modal">
                        <div
                            v-if="isSecondLayerOpen"
                            class="custom-select-modal"
                        >
                            <button
                                ref="backButtonRef"
                                class="back-button"
                                @click="toggleSecondLayer(subLayer.secondLayer, $event)"
                            >
                                <img
                                    src="~/assets/icons/chevron-right-black.svg"
                                    alt="close"
                                />
                                <span>{{ t('general.back') }}</span>
                            </button>

                            <div class="second-layer-group-wrapper">
                                <div class="second-layer-group">
                                    <NuxtLink
                                        v-slot="{ href, navigate }"
                                        :to="useStoryblokUrl(subLayer.link)"
                                        custom
                                    >
                                        <a
                                            :href="href"
                                            class="second-layer-link"
                                            :aria-disabled="
                                                isClient && isLocked(subLayer.link) && !isKeycloakAuthenticated
                                            "
                                            @click="wrapNavigate(navigate, subLayer.link).onClick($event)"
                                            @keydown.enter="wrapNavigate(navigate, subLayer.link).onEnter($event)"
                                            @keydown.space="wrapNavigate(navigate, subLayer.link).onSpace($event)"
                                        >
                                            {{ subLayer.linkName }}
                                            <template
                                                v-if="
                                                    isClient &&
                                                    subLayer.link?.story?.content?.locked &&
                                                    !isKeycloakAuthenticated
                                                "
                                            >
                                                <img
                                                    src="~/assets/icons/lock-keyhole.svg"
                                                    class="navi-lock"
                                                    aria-hidden="true"
                                                />
                                            </template>
                                            <template v-else>
                                                <img
                                                    src="~/assets/icons/chevron-right-black-thicker.svg"
                                                    alt="chevron"
                                                    aria-hidden="true"
                                                />
                                            </template>
                                        </a>
                                    </NuxtLink>
                                </div>
                            </div>

                            <div
                                v-for="secondLayerGroup in subLayer.secondLayer"
                                :key="secondLayerGroup._uid"
                                class="second-layer-group-wrapper"
                            >
                                <li
                                    v-for="secondLayerLink in secondLayerGroup.links"
                                    :key="secondLayerLink._uid"
                                    class="second-layer-group"
                                >
                                    <NuxtLink
                                        v-if="
                                            secondLayerLink?.link?.story?.full_slug || secondLayerLink?.link?.cached_url
                                        "
                                        v-slot="{ href, navigate }"
                                        :to="useStoryblokUrl(secondLayerLink.link)"
                                        custom
                                    >
                                        <a
                                            :href="href"
                                            class="second-layer-link"
                                            :aria-disabled="
                                                isClient && isLocked(secondLayerLink.link) && !isKeycloakAuthenticated
                                            "
                                            @click="wrapNavigate(navigate, secondLayerLink.link).onClick($event)"
                                            @keydown.enter="
                                                wrapNavigate(navigate, secondLayerLink.link).onEnter($event)
                                            "
                                            @keydown.space="
                                                wrapNavigate(navigate, secondLayerLink.link).onSpace($event)
                                            "
                                        >
                                            {{ secondLayerLink.linkName }}
                                            <template
                                                v-if="
                                                    secondLayerLink.link.story?.full_slug ||
                                                    secondLayerLink.link.cached_url
                                                "
                                            >
                                                <img
                                                    v-if="
                                                        isClient &&
                                                        secondLayerLink.link.story?.content?.locked &&
                                                        !isKeycloakAuthenticated
                                                    "
                                                    src="~/assets/icons/lock-keyhole.svg"
                                                    class="navi-lock"
                                                    aria-hidden="true"
                                                />
                                                <img
                                                    v-else
                                                    src="~/assets/icons/chevron-right-black-thicker.svg"
                                                    alt="chevron"
                                                    aria-hidden="true"
                                                />
                                            </template>
                                        </a>
                                    </NuxtLink>

                                    <div
                                        v-else
                                        class="second-layer-link"
                                    >
                                        {{ secondLayerLink.linkName }}
                                    </div>
                                    <ul
                                        v-if="secondLayerLink.thirdLayer && secondLayerLink.thirdLayer.length >= 1"
                                        class="third-layer"
                                    >
                                        <li
                                            v-for="thirdLayerLink in secondLayerLink.thirdLayer"
                                            :key="thirdLayerLink._uid"
                                        >
                                            <NuxtLink
                                                v-if="thirdLayerLink.component === 'ThirdLayerLink'"
                                                v-slot="{ href, navigate }"
                                                :to="useStoryblokUrl(thirdLayerLink.link)"
                                                custom
                                            >
                                                <a
                                                    :href="href"
                                                    class="third-layer-link"
                                                    :aria-disabled="
                                                        isClient &&
                                                        isLocked(thirdLayerLink.link) &&
                                                        !isKeycloakAuthenticated
                                                    "
                                                    @click="wrapNavigate(navigate, thirdLayerLink.link).onClick($event)"
                                                    @keydown.enter="
                                                        wrapNavigate(navigate, thirdLayerLink.link).onEnter($event)
                                                    "
                                                    @keydown.space="
                                                        wrapNavigate(navigate, thirdLayerLink.link).onSpace($event)
                                                    "
                                                >
                                                    {{ thirdLayerLink.linkName }}
                                                    <img
                                                        v-if="
                                                            isClient &&
                                                            thirdLayerLink.link?.story?.content?.locked &&
                                                            !isKeycloakAuthenticated
                                                        "
                                                        src="~/assets/icons/lock-keyhole.svg"
                                                        class="navi-lock"
                                                        aria-hidden="true"
                                                    />
                                                </a>
                                            </NuxtLink>

                                            <StoryblokComponentWrapper
                                                v-else
                                                :blok="thirdLayerLink"
                                                class="third-layer-link"
                                            />
                                        </li>
                                    </ul>
                                </li>
                            </div>

                            <ul
                                v-if="subLayer.additionalBlocks.length >= 1"
                                class="additional-blocks"
                                :class="{
                                    '--left': subLayer.additionalBlocksLeft
                                }"
                            >
                                <li
                                    v-for="additionalBlock in subLayer.additionalBlocks"
                                    :key="additionalBlock._uid"
                                >
                                    <StoryblokComponentWrapper
                                        :blok="additionalBlock"
                                        @click="closeMenu"
                                    />
                                </li>
                            </ul>
                        </div>
                    </Transition>
                </div>

                <div class="bottom">
                    <MetaNavigation
                        v-if="
                            story?.content.metaNavigationLinks?.length &&
                            story.content.metaNavigationLinks.length > 0 &&
                            !isCheckout &&
                            !isDesktop
                        "
                        :config="story.content"
                        @click="closeMenu"
                    />

                    <HeaderLanguagePicker
                        v-if="header.showLanguageSwitcher"
                        :modal-open="isMenuOpen"
                    />
                </div>
            </div>
        </Transition>
    </header>

    <StoryblokComponentWrapper
        v-for="blok in header?.cookieTeaser"
        :key="blok._uid"
        :blok="blok"
        :bg-color-class="bgColorClass"
    />

    <SharedLoginModal
        :login-modal-controller="loginModalController"
        modal-id="layout-header-login"
    />
</template>

<style scoped>
    @import '~/assets/scss/navigation/layout-header.scss';
    @import '~/assets/scss/navigation/header-navigation-mobile.scss';
</style>
